import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels, ordersLabels } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Info, Edit, Save, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useListingManagement } from "@/hooks/useListingManagement";

interface DeliveryFeeZones {
  zoneA: number;
  zoneB: number;
  zoneC: number;
  zoneD: number;
}

interface DeliveryFeePopupProps {
  deliveryFee: DeliveryFeeZones | null;
  listingId: string;
  onSave?: (fees: DeliveryFeeZones) => void;
  isAdmin?: boolean;
}

export const DeliveryFeePopup = ({
  deliveryFee,
  listingId,
  onSave,
  isAdmin = false,
}: DeliveryFeePopupProps) => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFees, setEditedFees] = useState<DeliveryFeeZones | null>(
    deliveryFee || null,
  );

  // Update editedFees when deliveryFee prop changes
  useEffect(() => {
    setEditedFees(deliveryFee || null);
  }, [deliveryFee]);

  const { updateListingAsync, isUpdatingListing } = useListingManagement({
    enableGetAllListings: isAdmin,
  });

  // Handle null deliveryFee
  if (!deliveryFee) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="flex items-center gap-2"
      >
        <Info className="h-4 w-4" />
        {o.delivery.noFees}
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2"
      >
        <Info className="h-4 w-4" />
        {o.delivery.viewFees}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{o.delivery.title}</DialogTitle>
            <DialogDescription>{o.delivery.description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-900">
              <div className="space-y-2">
                <h4 className="font-medium text-blue-500 dark:text-blue-200">
                  {o.delivery.zoneA}
                </h4>
                <p className="text-sm text-blue-500 dark:text-blue-200">
                  {o.delivery.zoneADesc}
                </p>
                {isEditing ? (
                  <div className="space-y-1">
                    <Label htmlFor="zoneA" className="text-sm">
                      {o.delivery.priceKr}
                    </Label>
                    <Input
                      id="zoneA"
                      type="number"
                      value={editedFees?.zoneA || 0}
                      onChange={(e) =>
                        setEditedFees((prev) => ({
                          ...(prev || {
                            zoneA: 0,
                            zoneB: 0,
                            zoneC: 0,
                            zoneD: 0,
                          }),
                          zoneA: parseInt(e.target.value) || 0,
                        }))
                      }
                      className="w-24"
                    />
                  </div>
                ) : (
                  <p className="text-lg font-bold text-blue-500 dark:text-blue-200">
                    {deliveryFee.zoneA} kr
                  </p>
                )}
              </div>
            </div>

            <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-900">
              <div className="space-y-2">
                <h4 className="font-medium text-green-500 dark:text-green-200">
                  {o.delivery.zoneB}
                </h4>
                <p className="text-sm text-green-500 dark:text-green-200">
                  {o.delivery.zoneBDesc}
                </p>
                {isEditing ? (
                  <div className="space-y-1">
                    <Label htmlFor="zoneB" className="text-sm">
                      {o.delivery.priceKr}
                    </Label>
                    <Input
                      id="zoneB"
                      type="number"
                      value={editedFees?.zoneB || 0}
                      onChange={(e) =>
                        setEditedFees((prev) => ({
                          ...(prev || {
                            zoneA: 0,
                            zoneB: 0,
                            zoneC: 0,
                            zoneD: 0,
                          }),
                          zoneB: parseInt(e.target.value) || 0,
                        }))
                      }
                      className="w-24"
                    />
                  </div>
                ) : (
                  <p className="text-lg font-bold text-green-500 dark:text-green-200">
                    {deliveryFee.zoneB} kr
                  </p>
                )}
              </div>
            </div>

            <div className="p-4 border rounded-lg bg-orange-50 dark:bg-orange-900">
              <div className="space-y-2">
                <h4 className="font-medium text-orange-800 dark:text-orange-200">
                  {o.delivery.zoneC}
                </h4>
                <p className="text-sm text-orange-600 dark:text-orange-200">
                  {o.delivery.zoneCDesc}
                </p>
                {isEditing ? (
                  <div className="space-y-1">
                    <Label htmlFor="zoneC" className="text-sm">
                      {o.delivery.priceKr}
                    </Label>
                    <Input
                      id="zoneC"
                      type="number"
                      value={editedFees?.zoneC || 0}
                      onChange={(e) =>
                        setEditedFees((prev) => ({
                          ...(prev || {
                            zoneA: 0,
                            zoneB: 0,
                            zoneC: 0,
                            zoneD: 0,
                          }),
                          zoneC: parseInt(e.target.value) || 0,
                        }))
                      }
                      className="w-24"
                    />
                  </div>
                ) : (
                  <p className="text-lg font-bold text-orange-800 dark:text-orange-200">
                    {deliveryFee.zoneC} kr
                  </p>
                )}
              </div>
            </div>

            <div className="p-4 border rounded-lg bg-red-50 dark:bg-red-900">
              <div className="space-y-2">
                <h4 className="font-medium text-red-800 dark:text-red-200">
                  {o.delivery.zoneD}
                </h4>
                <p className="text-sm text-red-600 dark:text-red-200">
                  {o.delivery.zoneDDesc}
                </p>
                {isEditing ? (
                  <div className="space-y-1">
                    <Label htmlFor="zoneD" className="text-sm">
                      {o.delivery.priceKr}
                    </Label>
                    <Input
                      id="zoneD"
                      type="number"
                      value={editedFees?.zoneD || 0}
                      onChange={(e) =>
                        setEditedFees((prev) => ({
                          ...(prev || {
                            zoneA: 0,
                            zoneB: 0,
                            zoneC: 0,
                            zoneD: 0,
                          }),
                          zoneD: parseInt(e.target.value) || 0,
                        }))
                      }
                      className="w-24"
                    />
                  </div>
                ) : (
                  <p className="text-lg font-bold text-red-800 dark:text-red-200">
                    {deliveryFee.zoneD > 0
                      ? `${deliveryFee.zoneD} kr`
                      : o.delivery.priceOnRequest}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="text-xs text-muted-foreground">{o.delivery.note}</div>

          {isAdmin && (
            <div className="flex justify-end gap-2 pt-4 border-t">
              {isEditing ? (
                <>
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (editedFees) {
                        try {
                          await updateListingAsync({
                            id: listingId,
                            deliveryFee: editedFees,
                          });
                          onSave?.(editedFees);
                          setIsEditing(false);
                        } catch (error) {}
                      }
                    }}
                    disabled={isUpdatingListing || !editedFees}
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {isUpdatingListing
                      ? o.invoicePopup.saving
                      : o.delivery.save}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditedFees(deliveryFee || null);
                      setIsEditing(false);
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    {o.delivery.cancel}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (!editedFees && deliveryFee) {
                      setEditedFees(deliveryFee);
                    }
                    setIsEditing(true);
                  }}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  {o.delivery.edit}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
